import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "@/components/state/store";
import {
  BackgroundTaskKind,
  removeBackgroundTask,
  setBackgroundTask,
} from "@/components/state/backgroundTasks/backgroundTasksSlice";
import { api } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import styles from "@/components/backgroundTasks/backgroundTaskManager.module.css";
import { TaskChannelMessageType } from "@/types/taskChannelTypes";
import { getBackgroundTaskFailure } from "@/components/backgroundTasks/backgroundTaskError";
import { BACKGROUND_TASK_CHANNEL_NAME } from "@/components/backgroundTasks/startBackgroundTask";
import CsvImportResult from "@/components/userAdministration/csvImportResult";
import BatchDocumentTaskResult from "@/components/backgroundTasks/batchDocumentTaskResult";
import BatchUploadTaskResult from "@/components/backgroundTasks/batchUploadTaskResult";

const BackgroundTaskManager = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showConfirmationModal } = useConfirmationModal();
  const tasks = useSelector((state: RootState) => state.backgroundTasks.tasks);

  const pollingTaskIds = useRef(new Set<string>());
  const handledByOtherTabs = useRef(new Set<string>());
  const taskChannel = useRef<BroadcastChannel | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    if (!taskChannel.current) {
      taskChannel.current = new BroadcastChannel(BACKGROUND_TASK_CHANNEL_NAME);
    }

    taskChannel.current.onmessage = (event) => {
      if (!isMounted.current) return;
      const { type, taskId, task, description, resData, kind } = event.data;
      if (type === TaskChannelMessageType.STARTED) {
        dispatch(setBackgroundTask({ ...task, isWatcher: true }));
      }

      if (type === TaskChannelMessageType.TRY_SHOW_MODAL) {
        if (document.visibilityState === "visible") {
          triggerModal(taskId, description, resData, kind);
          safePostMessage({
            type: TaskChannelMessageType.COMPLETED,
            taskId: taskId,
          });
        }
      }

      if (type === TaskChannelMessageType.COMPLETED) {
        handledByOtherTabs.current.add(taskId);
        dispatch(removeBackgroundTask(taskId));
      }
    };

    return () => {
      isMounted.current = false;
      taskChannel.current?.close();
      taskChannel.current = null;
    };
  }, [dispatch, t, showConfirmationModal]);
  const safePostMessage = (message: any) => {
    if (isMounted.current && taskChannel.current) {
      try {
        taskChannel.current.postMessage(message);
      } catch {
        // console.log("closed");
      }
    }
  };

  const triggerModal = (
    taskId: string,
    description: string,
    resData: any,
    kind?: BackgroundTaskKind
  ) => {
    if (kind === "certificate") {
      showConfirmationModal(t("certificate_ready_message"), {
        title: t("certificate_ready"),
        disableCancelButton: true,
      });
      return;
    }
    if (kind === "worker_csv") {
      showConfirmationModal(
        <CsvImportResult
          result={resData?.result}
          createdLabel={t("created_employees_entries")}
          updatedLabel={t("updated_employees_entries")}
        />,
        {
          title: t("worker_csv_import_completed"),
          disableCancelButton: true,
        }
      );
      return;
    }
    if (kind === "batch_document") {
      showConfirmationModal(
        <BatchDocumentTaskResult taskId={taskId} task={resData} />,
        {
          title: t("batch_document_task_completed"),
          disableCancelButton: true,
        }
      );
      return;
    }
    if (kind === "batch_upload") {
      showConfirmationModal(
        <BatchUploadTaskResult taskId={taskId} task={resData} />,
        {
          title: t("batch_upload_task_completed"),
          disableCancelButton: true,
        }
      );
      return;
    }
    showConfirmationModal(
      <div>
        <div className={styles.info_row_first}>
          <label>{t("task_complete")}</label>
        </div>
        <div className={styles.info_row}>
          <label>{t("description")}:</label>
          <span className={styles.value}>
            {description} - {resData?.name}
          </span>
        </div>
        <div className={styles.info_row}>
          <label>{t("id")}:</label>
          <span className={styles.id_value}>{taskId}</span>
        </div>
      </div>,
      {
        title: `${t("status_completed")}`,
        disableCancelButton: true,
      }
    );
  };

  useEffect(() => {
    const runningTasksToPoll = tasks.filter(
      (task) => task.status === "in_progress" && !task.isWatcher
    );

    runningTasksToPoll.forEach((task) => {
      if (!pollingTaskIds.current.has(task.id)) {
        pollingTaskIds.current.add(task.id);
        pollTask(task.id, task.description, task.kind);
      }
    });
  }, [tasks]);

  const pollTask = async (
    taskId: string,
    description: string,
    kind?: BackgroundTaskKind
  ) => {
    try {
      const maxRetries = 360;

      for (let i = 0; i < maxRetries; i++) {
        if (!isMounted.current || handledByOtherTabs.current.has(taskId)) {
          return;
        }

        const res: any = await new Promise((resolve, reject) => {
          api.genericApiRequest({
            entity: "taskTracking",
            entityId: taskId,
            // managers may only call wait_for_task_to_complete, not retrieve
            additionalRouteParts:
              kind === "worker_csv"
                ? { wait_for_task_to_complete: "/" }
                : undefined,
            disableGlobalSpinner: true,
            method: "get",
            successHandler: (response: any) => resolve(response),
            failHandler: (err: any) => reject(err),
          });
        });

        if (!isMounted.current) return;

        const status = res?.data?.status;

        if (status === "canceled" || status === "failed") {
          safePostMessage({ type: TaskChannelMessageType.COMPLETED, taskId });
          const failure = getBackgroundTaskFailure(
            res?.data,
            status === "canceled"
              ? t("create_document_canceled")
              : t("task_failed"),
            t
          );
          console.error("Background task failed", {
            taskTrackingId: taskId,
            ...failure.consoleDetails,
          });
          throw new Error(failure.message);
        }

        if (status === "completed") {
          const resData = res?.data;
          window.dispatchEvent(
            new CustomEvent("background-task-completed", { detail: { taskId } })
          );

          if (document.visibilityState === "visible") {
            safePostMessage({
              type: TaskChannelMessageType.COMPLETED,
              taskId: taskId,
            });
            triggerModal(taskId, description, resData, kind);
          } else {
            safePostMessage({
              type: TaskChannelMessageType.TRY_SHOW_MODAL,
              taskId,
              description,
              resData,
              kind,
            });

            setTimeout(() => {
              if (
                isMounted.current &&
                !handledByOtherTabs.current.has(taskId)
              ) {
                triggerModal(taskId, description, resData, kind);
                safePostMessage({
                  type: TaskChannelMessageType.COMPLETED,
                  taskId: taskId,
                });
              }
            }, 2000);
          }
          return;
        }

        await new Promise((r) => setTimeout(r, 5000));
      }

      throw new Error(`Task '${description}' timed out.`);
    } catch (error: any) {
      if (isMounted.current && !handledByOtherTabs.current.has(taskId)) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        showConfirmationModal(errorMessage, {
          title: t("error_occurred"),
          disableCancelButton: true,
        });
      }
    } finally {
      if (isMounted.current) {
        pollingTaskIds.current.delete(taskId);
        handledByOtherTabs.current.delete(taskId);
        dispatch(removeBackgroundTask(taskId));
      }
    }
  };

  return null;
};

export default BackgroundTaskManager;
